import React, { useState } from 'react';
import { Globe, Calculator, CreditCard } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрого выбора страны и типа визы
 */
const QuickSelectionSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  countries,
  visaTypes
}) => {
  // Состояние для активной вкладки
  const [activeTab, setActiveTab] = useState('countries');

  // Получаем только популярные страны и типы виз для быстрого выбора
  const popularCountries = countries.filter(country => country.isPopular);
  const popularVisaTypes = visaTypes.filter(visaType => visaType.isPopular);

  // Обработчик выбора страны
  const handleCountrySelect = (countryId) => {
    setFormData(prev => ({
      ...prev,
      country: countryId
    }));
  };

  // Обработчик выбора типа визы
  const handleVisaTypeSelect = (visaTypeId) => {
    setFormData(prev => ({
      ...prev,
      visaType: visaTypeId
    }));
  };

  // Функция для расчета
  const calculateFee = () => {
    // Прокручиваем к кнопке "Рассчитать" и кликаем на неё
    document.querySelector('button[type="submit"]')?.click();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Globe className="w-5 h-5 mr-2 text-purple-500" />
        Быстрый выбор визы
      </h3>

      {/* Вкладки для переключения между странами и типами виз */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('countries')}
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'countries'
              ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Популярные страны
        </button>
        <button
          onClick={() => setActiveTab('visaTypes')}
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'visaTypes'
              ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Типы виз
        </button>
      </div>

      {/* Содержимое активной вкладки */}
      <div>
        {/* Популярные страны */}
        {activeTab === 'countries' && (
          <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-2">
            {popularCountries.map(country => (
              <button
                key={country.id}
                onClick={() => handleCountrySelect(country.id)}
                className={`p-2 text-sm rounded-lg border transition-colors ${
                  formData.country === country.id
                    ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {country.name}
              </button>
            ))}
          </div>
        )}

        {/* Типы виз */}
        {activeTab === 'visaTypes' && (
          <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-2">
            {popularVisaTypes.map(visaType => (
              <button
                key={visaType.id}
                onClick={() => handleVisaTypeSelect(visaType.id)}
                className={`p-2 text-sm rounded-lg border transition-colors ${
                  formData.visaType === visaType.id
                    ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {visaType.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Секция дополнительных опций */}
      <div className="mt-5 space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Тип въезда</h4>
        <div className="grid grid-cols-3 gap-2">
          {['single', 'double', 'multiple'].map(entryType => {
            const labels = {
              'single': 'Однократный',
              'double': 'Двукратный',
              'multiple': 'Многократный'
            };
            
            return (
              <button
                key={entryType}
                onClick={() => setFormData(prev => ({ ...prev, entryType }))}
                className={`p-2 text-xs rounded-lg border transition-colors ${
                  formData.entryType === entryType
                    ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {labels[entryType]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Срок действия визы */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Срок действия визы</h4>
        <div className="grid grid-cols-2 gap-2">
          {[30, 90, 180, 365].map(days => (
            <button
              key={days}
              onClick={() => setFormData(prev => ({ ...prev, visaDuration: days.toString() }))}
              className={`p-2 text-xs rounded-lg border transition-colors ${
                formData.visaDuration === days.toString()
                  ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {days} дней
            </button>
          ))}
        </div>
      </div>

      {/* Время обработки */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Время обработки</h4>
        <div className="grid grid-cols-2 gap-2">
          {['standard', 'express'].map(processingTime => {
            const labels = {
              'standard': 'Стандартное',
              'express': 'Ускоренное'
            };
            
            return (
              <button
                key={processingTime}
                onClick={() => setFormData(prev => ({ ...prev, processingTime }))}
                className={`p-2 text-xs rounded-lg border transition-colors ${
                  formData.processingTime === processingTime
                    ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {labels[processingTime]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Количество заявителей */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Количество заявителей</h4>
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map(count => (
            <button
              key={count}
              onClick={() => setFormData(prev => ({ ...prev, applicantsCount: count.toString() }))}
              className={`p-2 text-xs rounded-lg border transition-colors ${
                formData.applicantsCount === count.toString()
                  ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {count}
            </button>
          ))}
        </div>
      </div>

      {/* Услуги (чекбоксы) */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Услуги</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.includeInsurance}
              onChange={e => setFormData(prev => ({ ...prev, includeInsurance: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Страховка</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.includeTranslation}
              onChange={e => setFormData(prev => ({ ...prev, includeTranslation: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Перевод документов</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.includeDelivery}
              onChange={e => setFormData(prev => ({ ...prev, includeDelivery: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Доставка паспорта</span>
          </label>
        </div>
      </div>

      {/* Кнопка расчета */}
      <div className="mt-6">
        <Button
          primary
          fullWidth
          onClick={calculateFee}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать стоимость
        </Button>
      </div>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
        Быстрый выбор основных параметров визы для расчета общей стоимости
      </div>
    </div>
  );
};

export default QuickSelectionSidebar;